import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

/**
 * Vitalora demo routing timeline (S8 of the build brief).
 *
 * The Luminos SPF30 routing timeline — 5 timestamped agent actions plus
 * the 24h/48h/72h escalation rules card. Pure render, no fetch — same
 * reasoning as `activityView.tsx`.
 */
interface TimelineStep {
  time: string;
  title: string;
  detail: string;
  state: "done" | "current" | "queued";
}

const TIMELINE: TimelineStep[] = [
  {
    time: "Day 1 · 16:50",
    title: "MEDIUM RISK — routed to James Rivera (Brand)",
    detail:
      "Brand flag raised on logo placement. Regulatory check passed. Routing path: Brand → Regulatory → Final sign-off.",
    state: "done",
  },
  {
    time: "Day 2 · 09:00",
    title: "Slack reminder sent (no response in 16h)",
    detail: "Auto-reminder fired against the 24h escalation rule.",
    state: "done",
  },
  {
    time: "Day 2 · 14:00",
    title: "James Rivera approved — auto-routed to Priya Patel",
    detail:
      "Brand approval logged with source attribution to Vitalora Brand Bible §3.2.",
    state: "done",
  },
  {
    time: "Day 2 · 14:01",
    title: "Priya Patel notified",
    detail: "Slack DM + Wrike task assignment posted with full review summary.",
    state: "done",
  },
  {
    time: "Now",
    title: "Awaiting Compliance sign-off",
    detail:
      "Currently with Priya Patel · 24h auto-reminder fires at 14:01 tomorrow if no response.",
    state: "current",
  },
];

const ESCALATION_RULES = [
  {
    threshold: "24h",
    action: "Slack reminder to current owner",
  },
  {
    threshold: "48h",
    action: "Escalate to Sarah Chen (Head of Marketing)",
  },
  {
    threshold: "72h",
    action: "Freeze asset and notify Legal + Compliance",
  },
];

const STATS = [
  { label: "Routed without senior review", value: "79%" },
  { label: "Routing decision time", value: "<60 sec" },
  { label: "Decisions logged", value: "100%" },
  { label: "Avg cycle time", value: "14d → 3d" },
];

export default function VitaloraAgentsRouting() {
  const { t } = useTranslation("ui");
  return (
    <Box className="mx-auto w-full max-w-[1100px] px-6 py-8">
      <Heading size="6">
        {t("vitaloraAgents.routingView.routingLuminosSpf30")}
      </Heading>
      <Text size="2" color="gray">
        Every action taken on this asset since submission. Routing is not a
        queue: the agent watches every asset after it leaves, follows up,
        escalates, and logs every decision.
      </Text>

      <Flex
        gap="6"
        className="mt-6"
        direction={{ initial: "column", md: "row" }}
      >
        <Box className="flex-1">
          <Flex direction="column" gap="2">
            {TIMELINE.map((step, idx) => (
              <Flex key={step.title} gap="3">
                <Flex direction="column" align="center" className="shrink-0">
                  <span
                    className={`size-3 rounded-full ${
                      step.state === "done"
                        ? "bg-puntt-accent-9"
                        : step.state === "current"
                          ? "bg-puntt-warn-9 animate-pulse"
                          : "bg-puntt-neutral-gray-7"
                    }`}
                  />
                  {idx < TIMELINE.length - 1 ? (
                    <span className="my-1 w-px flex-1 bg-puntt-neutral-gray-5" />
                  ) : null}
                </Flex>
                <Box className="flex-1 pb-4">
                  <Flex align="baseline" gap="2" justify="between">
                    <Text weight="bold">{step.title}</Text>
                    <Text size="1" color="gray">
                      {step.time}
                    </Text>
                  </Flex>
                  <Text size="2" color="gray">
                    {step.detail}
                  </Text>
                </Box>
              </Flex>
            ))}
          </Flex>
        </Box>

        <Box className="md:w-80">
          <div className="rounded-xl border border-puntt-neutral-gray-6 bg-base-white p-4 shadow-sm">
            <Heading size="3">
              {t("vitaloraAgents.routingView.escalationRules")}
            </Heading>
            <Text size="1" color="gray">
              {t(
                "vitaloraAgents.routingView.appliedAutomaticallyByRoutingAgent",
              )}
            </Text>
            <Flex direction="column" gap="2" mt="3">
              {ESCALATION_RULES.map((rule) => (
                <Flex key={rule.threshold} gap="2" align="start">
                  <span className="font-mono rounded-md bg-puntt-accent-3 px-2 py-0.5 text-xs text-puntt-accent-11">
                    {rule.threshold}
                  </span>
                  <Text size="2" color="gray">
                    {rule.action}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </div>

          <div className="mt-4 rounded-xl border border-puntt-neutral-gray-6 bg-base-white p-4 shadow-sm">
            <Heading size="3">
              {t("vitaloraAgents.routingView.routingStats")}
            </Heading>
            <Flex direction="column" gap="2" mt="3">
              {STATS.map((s) => (
                <Flex key={s.label} justify="between">
                  <Text size="2" color="gray">
                    {s.label}
                  </Text>
                  <Text size="2" weight="bold">
                    {s.value}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </div>
        </Box>
      </Flex>
    </Box>
  );
}
